/* import __COLOCATED_TEMPLATE__ from './scim.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';

import type IntlService from 'ember-intl/services/intl';
import type ScimManagementService from 'embercom/services/scim-management-service';
import type CopilotDataService from 'embercom/services/copilot-data-service';
import type AppService from 'embercom/services/app-service';
import { COPILOT_FREE_CREDIT_LIMIT } from 'embercom/services/copilot-usage-service';

import type { ScimGroup, ScimArgs, SeatType, CopilotSeat, Role } from './scim/types';
import { ScimGroupState } from './scim/types';
import {
  SCIM_TABLE_COLUMNS,
  CHANGES_REVIEW_TABLE_COLUMNS,
  getUserInitials,
  createRoleDropdownList,
  getGroupStateDisplayName,
  getGroupStateColor,
  type SeatSelectGroup,
  type CopilotSeatSelectItem,
} from './scim/utils';
import {
  getRoleNameById,
  type SelectItem,
  type SelectGroup,
} from 'embercom/utils/teammates/role-dropdown-utils';

export default class Scim extends Component<ScimArgs> {
  @service declare intl: IntlService;
  @service declare scimManagementService: ScimManagementService;
  @service declare copilotDataService: CopilotDataService;
  @service declare appService: AppService;

  private _roleSelectGroupListCache: SelectGroup[] | null = null;
  private _roleSelectGroupListKey = '';

  constructor(owner: unknown, args: ScimArgs) {
    super(owner, args);
    this.loadData();
  }

  private buildRoleSelectGroupList(): SelectGroup[] {
    let key = this.availableRoles.map((r) => r.id).join(',');
    if (this._roleSelectGroupListCache && this._roleSelectGroupListKey === key) {
      return this._roleSelectGroupListCache;
    }

    let unassignableRoles = this.scimManagementService.getUnassignableRolesInfo();

    let noRoleItem: SelectItem = {
      text: this.intl.t('settings.teammates.role.no-role'),
      value: null,
    };

    let roleItems: SelectItem[] = this.availableRoles.map((role) => ({
      text: role.name,
      value: role,
      isDisabled: unassignableRoles?.[role.id]?.isDisabled || false,
      tooltipText: unassignableRoles?.[role.id]?.tooltipText,
    }));

    this._roleSelectGroupListCache = [
      { items: [noRoleItem] },
      { heading: this.intl.t('settings.teammates.role.roles'), items: roleItems },
    ];
    this._roleSelectGroupListKey = key;

    return this._roleSelectGroupListCache;
  }

  get seatTypeSelectGroupList(): SeatSelectGroup[] {
    return [
      {
        items: [
          {
            value: null,
            text: this.intl.t('new-settings.workspace.teammates.scim-tab.seat-type.not-set'),
          },
          {
            value: 'core',
            text: this.intl.t('new-settings.workspace.teammates.scim-tab.seat-type.full'),
          },
          {
            value: 'light',
            text: this.intl.t('new-settings.workspace.teammates.scim-tab.seat-type.lite'),
          },
        ],
      },
    ];
  }

  getRoleHasCopilotPermission(groupId: string | number): boolean {
    let groupIdStr = groupId.toString();
    let group = this.scimGroups.find((g) => g.id === groupIdStr);

    let pendingRoleId = this.scimManagementService.changesState.roleChanges[groupIdStr];
    let effectiveRoleId = pendingRoleId !== undefined ? pendingRoleId : group?.role_id;

    if (effectiveRoleId === null || effectiveRoleId === undefined) {
      return true;
    }

    let role = this.availableRoles.find((r) => r.id === effectiveRoleId.toString());
    return role?.can_inbox__access_copilot ?? false;
  }

  get copilotSeatPrice(): string {
    let priceInCents = this.copilotDataService.copilotData?.price ?? 0;
    return this.intl.formatNumber(priceInCents / 100, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: priceInCents % 100 >= 1 ? 2 : 0,
    });
  }

  @action
  getCopilotSeatSelectGroupListForGroup(groupId: string | number): SeatSelectGroup[] {
    let roleHasCopilot = this.getRoleHasCopilotPermission(groupId);
    let groupIdStr = groupId.toString();
    let group = this.scimGroups.find((g) => g.id === groupIdStr);
    let pendingRoleId = this.scimManagementService.changesState.roleChanges[groupIdStr];
    let hasRole = pendingRoleId !== undefined ? pendingRoleId !== null : group?.role_id != null;

    let currentSeatType = this.getCurrentSeatTypeForGroup(groupId);
    let isLiteSeat = currentSeatType === 'light';

    let cannotDisableTooltip = this.intl.t(
      'new-settings.workspace.teammates.scim-tab.copilot-seat.cannot-disable-tooltip',
    );
    let cannotEnableTooltip = this.intl.t(
      'new-settings.workspace.teammates.scim-tab.copilot-seat.cannot-enable-tooltip',
    );
    let liteSeatNoCopilotTooltip = this.intl.t(
      'new-settings.workspace.teammates.scim-tab.tooltips.lite-seat-no-copilot',
    );

    let notSetDisabled = hasRole && roleHasCopilot;
    let notSetTooltip = notSetDisabled
      ? this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.must-configure-tooltip')
      : undefined;

    let offDisabled = hasRole && roleHasCopilot;
    let offTooltip = offDisabled ? cannotDisableTooltip : undefined;

    let includedDisabled = isLiteSeat || (hasRole && !roleHasCopilot);
    let includedTooltip: string | undefined;
    if (isLiteSeat) {
      includedTooltip = liteSeatNoCopilotTooltip;
    } else if (hasRole && !roleHasCopilot) {
      includedTooltip = cannotEnableTooltip;
    }

    let items: CopilotSeatSelectItem[] = [
      {
        value: null,
        text: this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.not-set'),
        isDisabled: notSetDisabled,
        tooltipText: notSetTooltip,
        component: 'table-cells/teammate-list/copilot-access-dropdown-item',
        componentAttrs: {
          description: this.intl.t(
            'new-settings.workspace.teammates.scim-tab.copilot-seat.not-set-description',
          ),
        },
      },
      {
        value: 'off',
        text: this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.off'),
        isDisabled: offDisabled,
        tooltipText: offTooltip,
        component: 'table-cells/teammate-list/copilot-access-dropdown-item',
        componentAttrs: {
          description: this.intl.t(
            'new-settings.workspace.teammates.scim-tab.copilot-seat.off-description',
          ),
        },
      },
      {
        value: 'included',
        text: this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.included'),
        isDisabled: includedDisabled,
        tooltipText: includedTooltip,
        component: 'table-cells/teammate-list/copilot-access-dropdown-item',
        componentAttrs: {
          description: this.intl.t(
            'new-settings.workspace.teammates.scim-tab.copilot-seat.included-description',
            { count: COPILOT_FREE_CREDIT_LIMIT },
          ),
        },
      },
      {
        value: 'unlimited',
        text: this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.unlimited'),
        isDisabled: includedDisabled,
        tooltipText: includedTooltip,
        component: 'table-cells/teammate-list/copilot-access-dropdown-item',
        componentAttrs: {
          description: this.intl.t(
            'new-settings.workspace.teammates.scim-tab.copilot-seat.unlimited-description',
            { price: this.copilotSeatPrice },
          ),
        },
      },
    ];

    return [{ items }];
  }

  get copilotSeatSelectGroupList(): SeatSelectGroup[] {
    if (this.scimManagementService.selectedGroup) {
      return this.getCopilotSeatSelectGroupListForGroup(
        this.scimManagementService.selectedGroup.id,
      );
    }
    return [
      {
        items: [
          {
            value: null,
            text: this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.not-set'),
            component: 'table-cells/teammate-list/copilot-access-dropdown-item',
            componentAttrs: {
              description: this.intl.t(
                'new-settings.workspace.teammates.scim-tab.copilot-seat.not-set-description',
              ),
            },
          },
          {
            value: 'off',
            text: this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.off'),
            component: 'table-cells/teammate-list/copilot-access-dropdown-item',
            componentAttrs: {
              description: this.intl.t(
                'new-settings.workspace.teammates.scim-tab.copilot-seat.off-description',
              ),
            },
          },
          {
            value: 'included',
            text: this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.included'),
            component: 'table-cells/teammate-list/copilot-access-dropdown-item',
            componentAttrs: {
              description: this.intl.t(
                'new-settings.workspace.teammates.scim-tab.copilot-seat.included-description',
                { count: COPILOT_FREE_CREDIT_LIMIT },
              ),
            },
          },
          {
            value: 'unlimited',
            text: this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.unlimited'),
            component: 'table-cells/teammate-list/copilot-access-dropdown-item',
            componentAttrs: {
              description: this.intl.t(
                'new-settings.workspace.teammates.scim-tab.copilot-seat.unlimited-description',
                { price: this.copilotSeatPrice },
              ),
            },
          },
        ],
      },
    ];
  }

  get availableRoles() {
    return this.args.roles || [];
  }

  get scimGroupsTableColumns() {
    return SCIM_TABLE_COLUMNS(this.intl, this.canSelectSeatType, this.canSelectCopilotSeat);
  }

  get changesReviewTableColumns() {
    return CHANGES_REVIEW_TABLE_COLUMNS(this.intl);
  }

  get canEditScim(): boolean {
    return this.scimManagementService.canEditScim;
  }

  get isLoadingEditPermission(): boolean {
    return this.scimManagementService.isLoadingEditPermission;
  }

  get hasUnsavedChanges(): boolean {
    return this.scimManagementService.changesState.hasChanges();
  }

  get scimGroups(): ScimGroup[] {
    return this.scimManagementService.currentState.groups;
  }

  get sortedScimGroups(): ScimGroup[] {
    return this.scimManagementService.changesState.sortedGroups;
  }

  get hasGroups(): boolean {
    return this.scimGroups.length > 0;
  }

  get hasCurrentGroupChanges(): boolean {
    if (!this.scimManagementService.selectedGroup) {
      return false;
    }
    let groupId = this.scimManagementService.selectedGroup.id;
    let hasRoleChange = this.scimManagementService.changesState.roleChanges[groupId] !== undefined;
    let hasSeatTypeChange =
      this.scimManagementService.changesState.seatTypeChanges[groupId] !== undefined;
    let hasCopilotSeatChange =
      this.scimManagementService.changesState.copilotSeatChanges[groupId] !== undefined;
    return hasRoleChange || hasSeatTypeChange || hasCopilotSeatChange;
  }

  get modalRoleDropdownList() {
    if (!this.scimManagementService.selectedGroup) {
      return [];
    }
    return createRoleDropdownList(this.availableRoles, this.intl, this.updateRoleInModal);
  }

  get modalRoleSelectGroupList(): SelectGroup[] {
    if (!this.scimManagementService.selectedGroup) {
      return [];
    }
    return this.buildRoleSelectGroupList();
  }

  get tableRoleSelectGroupList(): SelectGroup[] {
    return this.buildRoleSelectGroupList();
  }

  get currentModalRoleName(): string {
    if (!this.scimManagementService.selectedGroup) {
      return '';
    }

    let group = this.scimManagementService.selectedGroup;
    let roleChange = this.scimManagementService.changesState.roleChanges[group.id];
    let effectiveRoleId = roleChange !== undefined ? roleChange : group.role_id;

    return this.getRoleNameById(effectiveRoleId);
  }

  get pendingChangesForReview() {
    return this.scimManagementService.getPendingChangesForReview();
  }

  get hasIncludedChanges(): boolean {
    return this.scimManagementService.hasIncludedChanges();
  }

  @action
  canModifyGroupRole(groupId: string): boolean {
    return this.scimManagementService.canModifyGroupRole(groupId);
  }

  @action
  getDisabledGroupRoleTooltip(groupId: string): string | null {
    if (this.canModifyGroupRole(groupId)) {
      return null;
    }
    return this.intl.t('new-settings.workspace.teammates.scim-tab.tooltips.cannot-modify-role');
  }

  get groupChanges() {
    return this.scimManagementService.changesState.roleChanges;
  }

  get seatTypeChanges() {
    return this.scimManagementService.changesState.seatTypeChanges;
  }

  get copilotSeatChanges() {
    return this.scimManagementService.changesState.copilotSeatChanges;
  }

  get canSelectSeatType(): boolean {
    return this.scimManagementService.canSelectSeatType;
  }

  get canSelectCopilotSeat(): boolean {
    return this.scimManagementService.canSelectCopilotSeat;
  }

  get isStandalone(): boolean {
    return this.scimManagementService.isStandalone;
  }

  get standaloneCopilotSeatSelectGroupList(): SeatSelectGroup[] {
    return [
      {
        items: [
          {
            value: 'unlimited',
            text: this.intl.t(
              'new-settings.workspace.teammates.scim-tab.copilot-seat.yes-assign-seat',
            ),
            component: 'new-settings/workspace/teammates/scim/standalone-copilot-dropdown-item',
            componentAttrs: {
              description: this.intl.t(
                'new-settings.workspace.teammates.scim-tab.copilot-seat.yes-assign-seat',
              ),
              copilotSeatPrice: this.copilotSeatPrice,
              icon: 'person',
            },
          },
          {
            value: 'off',
            text: this.intl.t(
              'new-settings.workspace.teammates.scim-tab.copilot-seat.no-do-not-assign',
            ),
            component: 'new-settings/workspace/teammates/scim/standalone-copilot-dropdown-item',
            componentAttrs: {
              description: this.intl.t(
                'new-settings.workspace.teammates.scim-tab.copilot-seat.no-do-not-assign',
              ),
              icon: 'remove-participant',
            },
          },
        ],
      },
    ];
  }

  @action
  getStandaloneCopilotSeatText(value: CopilotSeat): string {
    return value === 'unlimited'
      ? this.intl.t('table-cells.teammate-list.standalone-copilot-access-cell.yes')
      : this.intl.t('table-cells.teammate-list.standalone-copilot-access-cell.no');
  }

  get showDetailsModal(): boolean {
    return this.scimManagementService.showDetailsModal;
  }

  get showApplyChangesModal(): boolean {
    return this.scimManagementService.showApplyChangesModal;
  }

  get showGroupDeactivationConfirmationModal(): boolean {
    return this.scimManagementService.showGroupDeactivationConfirmationModal;
  }

  get selectedGroupForDetails(): ScimGroup | null {
    return this.scimManagementService.selectedGroup;
  }

  get canDeactivateSelectedGroup(): boolean {
    let selectedGroup = this.scimManagementService.selectedGroup;
    return Boolean(
      selectedGroup && selectedGroup.state === ScimGroupState.ACTIVE && this.canEditScim,
    );
  }

  get currentRoleNameForDeactivation(): string {
    if (!this.scimManagementService.selectedGroup) {
      return '';
    }
    let group = this.scimManagementService.selectedGroup;
    return this.getRoleNameById(group.role_id);
  }

  get isLoadingGroupDetails(): boolean {
    return this.scimManagementService.isLoadingGroupDetails;
  }

  get isLoading(): boolean {
    return this.scimManagementService.isLoading;
  }

  get isLoadingUserGroups(): boolean {
    return this.scimManagementService.isLoadingUserGroups;
  }

  @action
  getCurrentRoleForGroup(groupId: string | number): number | null {
    let group = this.scimGroups.find((g) => g.id === groupId.toString());
    let roleChange = this.scimManagementService.changesState.roleChanges[groupId.toString()];
    return roleChange !== undefined ? roleChange : (group?.role_id ?? null);
  }

  @action
  getCurrentRoleObjectForGroup(groupId: string | number): Role | null {
    let roleId = this.getCurrentRoleForGroup(groupId);
    if (roleId === null || roleId === undefined) {
      return null;
    }
    let roleIdStr = roleId.toString();
    return this.availableRoles.find((r) => r.id === roleIdStr) || null;
  }

  @action
  findRoleInList(list: SelectGroup[], groupId: string | number): Role | null {
    let roleId = this.getCurrentRoleForGroup(groupId);
    let allItems = list.flatMap((group) => group.items);

    if (roleId === null || roleId === undefined) {
      return null;
    }

    let roleIdStr = roleId.toString();
    let item = allItems.find((item) => item.value?.id === roleIdStr);
    return item?.value || null;
  }

  get currentModalRoleObject(): Role | null {
    if (!this.scimManagementService.selectedGroup) {
      return null;
    }

    let group = this.scimManagementService.selectedGroup;
    let roleChange = this.scimManagementService.changesState.roleChanges[group.id];
    let effectiveRoleId = roleChange !== undefined ? roleChange : group.role_id;

    if (effectiveRoleId === null || effectiveRoleId === undefined) {
      return null;
    }
    let roleIdStr = effectiveRoleId.toString();
    return this.availableRoles.find((r) => r.id === roleIdStr) || null;
  }

  @action
  getCurrentSeatTypeForGroup(groupId: string | number): SeatType {
    let group = this.scimGroups.find((g) => g.id === groupId.toString());
    let seatTypeChange =
      this.scimManagementService.changesState.seatTypeChanges[groupId.toString()];
    if (seatTypeChange !== undefined) {
      return seatTypeChange;
    }
    return (group?.seat_type as SeatType) ?? null;
  }

  @action
  getCurrentCopilotSeatForGroup(groupId: string | number): CopilotSeat {
    let group = this.scimGroups.find((g) => g.id === groupId.toString());
    let copilotSeatChange =
      this.scimManagementService.changesState.copilotSeatChanges[groupId.toString()];
    // Return the actual value including null, don't default to 'off'
    let currentValue: CopilotSeat =
      copilotSeatChange !== undefined ? copilotSeatChange : (group?.copilot_seat as CopilotSeat);

    // Standalone apps: user selection is final, no seat-type or role-based overrides
    if (this.isStandalone) {
      return currentValue;
    }

    let currentSeatType = this.getCurrentSeatTypeForGroup(groupId);
    if (currentSeatType === 'light') {
      return 'off';
    }

    let roleHasCopilot = this.getRoleHasCopilotPermission(groupId);
    let groupIdStr = groupId.toString();
    let pendingRoleId = this.scimManagementService.changesState.roleChanges[groupIdStr];
    let hasRole = pendingRoleId !== undefined ? pendingRoleId !== null : group?.role_id != null;

    if (currentValue === 'off' && hasRole && roleHasCopilot) {
      return 'included';
    }

    if (
      (currentValue === 'included' || currentValue === 'unlimited') &&
      hasRole &&
      !roleHasCopilot
    ) {
      return 'off';
    }

    return currentValue;
  }

  get currentModalSeatType(): SeatType {
    if (!this.scimManagementService.selectedGroup) {
      return 'core';
    }
    return this.getCurrentSeatTypeForGroup(this.scimManagementService.selectedGroup.id);
  }

  get currentModalCopilotSeat(): CopilotSeat {
    if (!this.scimManagementService.selectedGroup) {
      return null;
    }
    return this.getCurrentCopilotSeatForGroup(this.scimManagementService.selectedGroup.id);
  }

  get currentModalCopilotSeatDisplayText(): string {
    if (!this.scimManagementService.selectedGroup) {
      return this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.not-set');
    }
    return this.getCopilotSeatDisplayText(this.scimManagementService.selectedGroup.id);
  }

  @action
  isCopilotDisabledForGroup(groupId: string): boolean {
    // Standalone apps don't have seat types - copilot is never disabled due to lite seats
    if (this.isStandalone) {
      return false;
    }
    let currentSeatType = this.getCurrentSeatTypeForGroup(groupId);
    return currentSeatType === 'light';
  }

  get isCopilotDisabledForSelectedGroup(): boolean {
    if (!this.scimManagementService.selectedGroup) {
      return false;
    }
    return this.isCopilotDisabledForGroup(this.scimManagementService.selectedGroup.id);
  }

  @action
  getCopilotDisabledTooltip(groupId: string): string | null {
    if (this.isCopilotDisabledForGroup(groupId)) {
      return this.intl.t('new-settings.workspace.teammates.scim-tab.tooltips.lite-seat-no-copilot');
    }
    return null;
  }

  get copilotDisabledTooltip(): string | null {
    if (!this.scimManagementService.selectedGroup) {
      return null;
    }
    return this.getCopilotDisabledTooltip(this.scimManagementService.selectedGroup.id);
  }

  @action
  onSelectSeatTypeForGroup(
    groupId: string | number,
    seatTypeOrItem: SeatType | { value: SeatType },
  ) {
    let seatType =
      typeof seatTypeOrItem === 'object' && seatTypeOrItem !== null && 'value' in seatTypeOrItem
        ? seatTypeOrItem.value
        : seatTypeOrItem;

    let copilotSeatBefore = this.getCurrentCopilotSeatForGroup(groupId);
    this.scimManagementService.updateSeatType(groupId.toString(), seatType ?? null);
    let copilotSeatAfter = this.getCurrentCopilotSeatForGroup(groupId);

    if (copilotSeatBefore !== copilotSeatAfter) {
      this.scimManagementService.updateCopilotSeat(groupId.toString(), copilotSeatAfter);
    }
  }

  @action
  onSelectCopilotSeatForGroup(
    groupId: string | number,
    copilotSeatOrItem: CopilotSeat | { value: CopilotSeat },
  ) {
    let copilotSeat =
      typeof copilotSeatOrItem === 'object' &&
      copilotSeatOrItem !== null &&
      'value' in copilotSeatOrItem
        ? copilotSeatOrItem.value
        : copilotSeatOrItem;
    this.scimManagementService.updateCopilotSeat(groupId.toString(), copilotSeat ?? null);
  }

  @action
  onSelectModalSeatType(seatTypeOrItem: SeatType | { value: SeatType }) {
    if (!this.scimManagementService.selectedGroup) {
      return;
    }
    this.onSelectSeatTypeForGroup(this.scimManagementService.selectedGroup.id, seatTypeOrItem);
  }

  @action
  onSelectModalCopilotSeat(copilotSeatOrItem: CopilotSeat | { value: CopilotSeat }) {
    if (!this.scimManagementService.selectedGroup) {
      return;
    }
    this.onSelectCopilotSeatForGroup(
      this.scimManagementService.selectedGroup.id,
      copilotSeatOrItem,
    );
  }

  @action
  getCurrentRoleNameForGroup(groupId: string | number): string {
    let roleId = this.getCurrentRoleForGroup(groupId);
    return this.getRoleNameById(roleId);
  }

  private getRoleNameById(roleId: number | null): string {
    return getRoleNameById(roleId, this.availableRoles, this.intl);
  }

  @action
  getRoleDropdownList(groupId: string | number) {
    return createRoleDropdownList(this.availableRoles, this.intl, (roleId) =>
      this.updateGroupRole(groupId, roleId),
    );
  }

  @action
  updateGroupRole(groupId: string | number, roleId: number | null) {
    let copilotSeatBefore = this.getCurrentCopilotSeatForGroup(groupId);
    this.scimManagementService.updateRole(groupId.toString(), roleId);
    let copilotSeatAfter = this.getCurrentCopilotSeatForGroup(groupId);

    if (copilotSeatBefore !== copilotSeatAfter) {
      this.scimManagementService.updateCopilotSeat(groupId.toString(), copilotSeatAfter);
    }
  }

  @action
  getUserInitials(user: Parameters<typeof getUserInitials>[0]) {
    return getUserInitials(user);
  }

  @action
  isUserExempt(userId: string): boolean {
    return this.scimManagementService.immuneAdminIds.includes(userId);
  }

  @action
  getGroupStateDisplayName(state: string | null) {
    return getGroupStateDisplayName(state, this.intl);
  }

  @action
  getGroupStateColor(state: string | null) {
    return getGroupStateColor(state);
  }

  @action
  getGroupPriority(group: ScimGroup): string {
    if (group.state === ScimGroupState.INACTIVE) {
      return '-';
    }
    return String(this.sortedScimGroups.findIndex((g) => g.id === group.id) + 1);
  }

  @action
  reorderGroups(reorderedGroups: ScimGroup[]) {
    if (!this.canEditScim) {
      return;
    }
    this.scimManagementService.reorderGroups(reorderedGroups);
  }

  @action
  async openGroupDetails(group: ScimGroup) {
    this.scimManagementService.openGroupDetails(group);

    try {
      await this.scimManagementService.loadGroupDetails(parseInt(group.id, 10));
    } catch (error) {
      console.error('Failed to load group details:', error);
    }
  }

  @action
  closeDetailsModal() {
    this.scimManagementService.closeGroupDetails();
  }

  @action
  updateRoleInModal(roleId: number | null) {
    if (!this.scimManagementService.selectedGroup) {
      return;
    }

    this.updateGroupRole(this.scimManagementService.selectedGroup.id, roleId);
  }

  @action
  onSelectRoleForGroup(groupId: string | number, role: Role | null) {
    let roleId = role?.id ? parseInt(role.id, 10) : null;
    this.updateGroupRole(groupId, roleId);
  }

  @action
  onSelectModalRole(role: Role | null) {
    let roleId = role?.id ? parseInt(role.id, 10) : null;
    this.updateRoleInModal(roleId);
  }

  @action
  async applyCurrentGroupChanges() {
    if (!this.scimManagementService.selectedGroup) {
      return;
    }

    let groupId = this.scimManagementService.selectedGroup.id;
    let hasRoleChange = this.scimManagementService.changesState.roleChanges[groupId] !== undefined;
    let hasSeatTypeChange =
      this.scimManagementService.changesState.seatTypeChanges[groupId] !== undefined;
    let hasCopilotSeatChange =
      this.scimManagementService.changesState.copilotSeatChanges[groupId] !== undefined;
    let hasChanges = hasRoleChange || hasSeatTypeChange || hasCopilotSeatChange;

    if (!hasChanges) {
      return;
    }

    try {
      await this.scimManagementService.applyChanges();
      this.scimManagementService.closeGroupDetails();
    } catch (error) {
      console.error('Failed to apply group changes:', error);
    }
  }

  @action
  openApplyChangesModal() {
    if (!this.canEditScim || !this.hasUnsavedChanges) {
      return;
    }
    this.scimManagementService.openApplyChangesModal();
  }

  @action
  closeApplyChangesModal() {
    this.scimManagementService.closeApplyChangesModal();
  }

  @action
  async confirmApplyChanges() {
    if (!this.canEditScim || !this.hasIncludedChanges) {
      this.closeApplyChangesModal();
      return;
    }

    await this.handleConfirmationApproved(() => this.closeApplyChangesModal());
  }

  @action
  toggleGroupChange(groupId: string): void {
    this.scimManagementService.toggleGroupChange(groupId);
  }

  @action
  toggleOrderChanges(): void {
    this.scimManagementService.toggleOrderChanges();
  }

  @action
  toggleSeatChange(changeId: string): void {
    this.scimManagementService.toggleSeatChange(changeId);
  }

  @action
  applyChanges() {
    this.openApplyChangesModal();
  }

  @action
  cancelChanges() {
    this.scimManagementService.resetChanges();
  }

  @action
  openGroupDeactivationConfirmation() {
    this.scimManagementService.openGroupDeactivationConfirmation();
  }

  @action
  closeGroupDeactivationConfirmation() {
    this.scimManagementService.closeGroupDeactivationConfirmation();
  }

  @action
  async confirmGroupDeactivation() {
    let selectedGroup = this.scimManagementService.selectedGroup;
    if (!selectedGroup) {
      return;
    }

    try {
      await this.scimManagementService.deactivateGroup(selectedGroup.id);
      this.scimManagementService.closeGroupDeactivationConfirmation();
      this.scimManagementService.closeGroupDetails();
    } catch (error) {
      console.error('Failed to deactivate group:', error);
    }
  }

  @action
  getCopilotSeatDisplayText(groupId: string | number): string {
    let copilotSeat = this.getCurrentCopilotSeatForGroup(groupId);
    switch (copilotSeat) {
      case 'unlimited':
        return this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.unlimited');
      case 'included':
        return this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.included');
      case 'off':
        return this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.off');
      default:
        return this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.not-set');
    }
  }

  private async loadData() {
    await this.scimManagementService.loadData(this.availableRoles);
  }

  private async handleConfirmationApproved(onSuccess: () => void) {
    try {
      await this.scimManagementService.applyChanges();
      onSuccess();
    } catch (error) {
      console.error('Failed to apply changes:', error);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Teammates::Scim': typeof Scim;
    'new-settings/workspace/teammates/scim': typeof Scim;
  }
}
